import {
  Box, Button, Divider, Grid, Paper, TextField, Typography,
} from '@material-ui/core';
import { Alert, Autocomplete, Skeleton } from '@material-ui/lab';
import * as React from 'react';
import {
  KeyboardDatePicker,
} from '@material-ui/pickers';
import axios from 'axios';
import useSWR from 'swr';
import ChipSelect from '../ChipSelect';
import type {
  ICompany, IRefineResultsProps, IRefineResultsResult, IRepairItemType, IStore,
} from './RepairLookupInterfaces';
import RepairLookupCtx from './RepairLookupContext';
import getBinNames from '../../lib/SWRfetchers/getBinNames';

interface IMockProps {
  handleRefineResults: (filterSetParam: IRefineResultsProps) => void;
  handleEmptyFilterSet: () => void;
  loading: boolean;
}

const RefineResults: React.FC<IMockProps> = ({ handleRefineResults, handleEmptyFilterSet, loading }) => {
  const repairLookupCtx = React.useContext(RepairLookupCtx);
  const [selectedCompany, setSelectedCompany] = React.useState<ICompany | null>(null);

  const fetcher = (url: string, companyId?: string) => axios.get<IRefineResultsResult>(url, {
    params: {
      companyId,
    },
  })
    .then((response) => response.data);

  const { binNames } = getBinNames();

  const { data } = useSWR(
    ['/repairlookup/getrefineresultsfilters', selectedCompany?.companyId],
    (url, companyId) => fetcher(url, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    },
  );

  const handleRepairTypeChange = (repairItem: IRepairItemType) => {
    const updatedRepairItem = repairLookupCtx?.filterSet?.repairType === repairItem ? { repairTypeName: '', repairTypeValue: 0 } : repairItem;
    repairLookupCtx?.setFilterSet({ ...repairLookupCtx?.filterSet, repairType: updatedRepairItem });
  };

  return (
    <>
      <Alert severity="info">{'Refine your repair results with the filters below'}</Alert>
      <Box my={2}>
        <Paper>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box pl={2}>
                <Typography variant="h5">{'Refine results'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Box pl={2}>
                <Typography variant="h6">{'Repair Type'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box pl={2}>
                <Grid
                  item
                  container
                  justifyContent="flex-start"
                  spacing={2}
                >
                  {!data
                    ? (
                      <Grid item>
                        <Skeleton>
                          <ChipSelect
                            label=""
                            selected
                            handleClick={() => { }}
                          />
                        </Skeleton>
                      </Grid>
                    )
                    : data?.repairItemTypes.map((e) => (
                      <Grid item>
                        <ChipSelect
                          key={e.repairTypeName}
                          label={e.repairTypeName}
                          selected={repairLookupCtx?.filterSet.repairType.repairTypeName === e.repairTypeName}
                          handleClick={() => handleRepairTypeChange(e)}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={4}>
              <Box pl={2}>
                <TextField
                  label="Repair Barcode"
                  variant="outlined"
                  fullWidth
                  value={repairLookupCtx?.filterSet?.repairBarcode}
                  onChange={(e) => { repairLookupCtx?.setFilterSet({ ...repairLookupCtx?.filterSet, repairBarcode: e.target.value }); }}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box pr={2}>
                <TextField
                  label="Store Repair Number"
                  variant="outlined"
                  fullWidth
                  value={repairLookupCtx?.filterSet?.storeRepairNumber}
                  onChange={(e) => { repairLookupCtx?.setFilterSet({ ...repairLookupCtx?.filterSet, storeRepairNumber: e.target.value }); }}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box pr={2} pb={2}>
                { binNames ? (
                  <Autocomplete
                    autoHighlight
                    autoSelect
                    options={binNames || []}
                    getOptionLabel={(option) => option.value}
                    getOptionSelected={(option, value) => option.value === value.value}
                    // prop spreading is directly from MUI tsx example
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    renderInput={(params) => <TextField {...params} label="Location" variant="outlined" />}
                    value={binNames.find((x) => x.value === repairLookupCtx?.filterSet?.location)}
                    onChange={(event, value) => {
                      const updatedStore = value?.value;
                      repairLookupCtx?.setFilterSet({ ...repairLookupCtx?.filterSet, location: updatedStore || '' });
                    }}
                  />
                ) : (
                  <Skeleton width="100%" height="56px" variant="rect" />
                )}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box pl={2} pb={2}>
                <Autocomplete
                  onChange={(event, value) => {
                    const updatedCompany = value || { companyId: '', companyName: '' };
                    setSelectedCompany(updatedCompany);
                    repairLookupCtx?.setFilterSet({ ...repairLookupCtx?.filterSet, company: updatedCompany });
                  }}
                  autoHighlight
                  autoSelect
                  options={data?.companies || new Array<ICompany>()}
                  getOptionLabel={(option) => option.companyName}
                  // prop spreading is directly from MUI tsx example
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  renderInput={(params) => <TextField {...params} label="Company" variant="outlined" />}
                  value={repairLookupCtx?.filterSet?.company}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box pr={2} pb={2}>
                <Autocomplete
                  autoHighlight
                  autoSelect
                  options={data?.stores || new Array<IStore>()}
                  getOptionLabel={(option) => option.storeName}
                  // prop spreading is directly from MUI tsx example
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  renderInput={(params) => <TextField {...params} label="Store" variant="outlined" />}
                  value={repairLookupCtx?.filterSet?.store}
                  onChange={(event, value) => {
                    const updatedStore = value || { storeId: '', storeName: '' };
                    repairLookupCtx?.setFilterSet({ ...repairLookupCtx?.filterSet, store: updatedStore });
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box pr={2}>
                <KeyboardDatePicker
                  autoOk
                  disableToolbar
                  disableFuture
                  format="MM/dd/yyyy"
                  id="date-picker-inline"
                  value={repairLookupCtx?.filterSet?.startDate}
                  maxDate={repairLookupCtx?.filterSet?.endDate}
                  onChange={(date, value) => { if (value) { repairLookupCtx?.setFilterSet({ ...repairLookupCtx?.filterSet, startDate: new Date(value) }); } }}
                  // prop spreading is directly from MUI tsx example
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  TextFieldComponent={(params) => <TextField {...params} fullWidth label="Start Date" variant="outlined" />}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box pl={2}>
                <KeyboardDatePicker
                  autoOk
                  disableToolbar
                  disableFuture
                  format="MM/dd/yyyy"
                  id="date-picker-inline"
                  value={repairLookupCtx?.filterSet?.endDate}
                  minDate={repairLookupCtx?.filterSet?.startDate}
                  onChange={(date, value) => { if (value) { repairLookupCtx?.setFilterSet({ ...repairLookupCtx?.filterSet, endDate: new Date(value) }); } }}
                  // prop spreading is directly from MUI tsx example
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  TextFieldComponent={(params) => <TextField {...params} fullWidth label="End Date" variant="outlined" />}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box pr={2} />
            </Grid>
            <Grid item container spacing={3} justifyContent="flex-end">
              <Box m={2}>
                <Grid item xs={12}>
                  <Box display="inline" mx={4}>
                    <Button variant="text" color="secondary" onClick={handleEmptyFilterSet}>
                      {'Clear'}
                    </Button>
                  </Box>
                  <Box display="inline" mx={4}>
                    <Button disabled={loading} variant="contained" color="primary" onClick={() => { if (repairLookupCtx?.filterSet) { handleRefineResults(repairLookupCtx?.filterSet); } }}>
                      {'Refine Results'}
                    </Button>
                  </Box>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

export default RefineResults;
